import React from 'react';

import { useSelector } from 'react-redux';

import { BuildStatusCard } from 'src/components/build-status-card';
import CurrentDiffFileTree from 'src/components/diff-set/diff-file-tree';
import { RunPipelineModalFromBranch } from 'src/components/pipelines/pages/modals/run-pipeline-from-branch-sidebar';
import { withRunPipelineModal } from 'src/components/pipelines/pages/modals/run-pipeline-modal';
import { Cards, SidebarContent } from 'src/components/sidebar';
import {
  getConflictCount,
  getUntruncatedPullRequestDiffFileCount,
} from 'src/redux/pull-request/selectors';
import {
  getBranchViewSourceBranchName,
  getBranchViewDestinationBranchName,
  getBranchViewDestinationRepoName,
  getBranchViewSourceBranchHash,
} from 'src/redux/repository/branch/branch-redux';
import { getBranchSidebarTabIndex } from 'src/redux/sidebar';
import SidebarTabs, {
  SidebarTabDetails,
} from 'src/sections/global/components/sidebar-tabs';
import Sidebar from 'src/sections/global/containers/sidebar';
import PrListCard from 'src/sections/repository/components/prlist-card';
import SidebarKeyboardShortcuts from 'src/sections/repository/components/sidebar-keyboard-shortcuts';
import { BranchPrListResponse } from 'src/sections/repository/sections/branch/hooks/branch-pr-list';
import urls from 'src/sections/repository/urls';
import { getCurrentRepository } from 'src/selectors/repository-selectors';

import {
  useSidebarExpandedWidth,
  useIsCollapsed,
  useResizeSidebar,
  useToggleSidebar,
} from '../hooks/sidebar-hooks';

import { BranchBuildStatusProvider } from './branch-build-provider';

export const useSidebarTabsDetails = (): SidebarTabDetails => {
  const conflictsCount: number = useSelector(getConflictCount);
  const sidebarTabIndex: number = useSelector(getBranchSidebarTabIndex);
  const untruncatedFileCount: number = useSelector(
    getUntruncatedPullRequestDiffFileCount
  );
  return { conflictsCount, sidebarTabIndex, untruncatedFileCount };
};

const BRANCH_PRLIST_CARD_COLLAPSED_LOCALSTORAGE_KEY =
  'branch.prlist.card.collapsed';

export type BranchSidebarProps = {
  branchPrList: BranchPrListResponse;
};

const BranchSidebar = (props: BranchSidebarProps): JSX.Element => {
  // We are passing this to the sidebar, which adds a `key` for us
  const expandedContent: SidebarContent[] = [];
  const collapsedContent: SidebarContent[] = [];
  const branchName = useSelector(getBranchViewSourceBranchName) || '';
  const branchTargetHash = useSelector(getBranchViewSourceBranchHash) || '';
  const destRepoName = useSelector(getBranchViewDestinationRepoName) || '';
  const destBranchName = useSelector(getBranchViewDestinationBranchName) || '';
  const repository = useSelector(getCurrentRepository);
  const ownerString =
    repository && repository.workspace ? repository.workspace.slug || '' : '';
  const createPrPath =
    repository &&
    `${urls.ui.createPullRequest(
      ownerString,
      repository.slug || '',
      branchName,
      destRepoName,
      destBranchName
    )}&event_source=branch_detail`;

  expandedContent.push(
    { type: Cards.BuildStatus, content: <BuildStatusCard /> },
    {
      type: Cards.PRList,
      content: (
        <PrListCard
          localStorageKey={BRANCH_PRLIST_CARD_COLLAPSED_LOCALSTORAGE_KEY}
          createPrPath={createPrPath}
          branchPrList={props.branchPrList}
        />
      ),
    },
    {
      type: Cards.Pipelines,
      content: (
        <RunPipelineModalFromBranch
          branchHash={branchTargetHash}
          branchName={branchName}
        />
      ),
    }
  );

  collapsedContent.push(
    {
      type: Cards.BuildStatus,
      content: <BuildStatusCard isCollapsed />,
    },
    {
      type: Cards.PRList,
      content: (
        <PrListCard
          localStorageKey={BRANCH_PRLIST_CARD_COLLAPSED_LOCALSTORAGE_KEY}
          createPrPath={createPrPath}
          branchPrList={props.branchPrList}
          isSidebarCollapsed
        />
      ),
    },
    {
      type: Cards.Pipelines,
      content: (
        <RunPipelineModalFromBranch
          branchHash={branchTargetHash}
          branchName={branchName}
          isCollapsed
        />
      ),
    }
  );

  const tabs = (
    <SidebarTabs
      key="sidebarTabs"
      expandedContent={expandedContent}
      expandedFileContent={<CurrentDiffFileTree />}
      sidebarTabsDetails={useSidebarTabsDetails}
      sidebarType="branch"
    />
  );

  return (
    <SidebarKeyboardShortcuts
      sidebarType="branch"
      toggleSidebar={useToggleSidebar()}
    >
      <BranchBuildStatusProvider>
        <Sidebar
          expandedWidth={useSidebarExpandedWidth()}
          collapsedContent={collapsedContent}
          expandedContent={expandedContent}
          isCollapsed={!!useIsCollapsed()}
          tabs={tabs}
          onResize={useResizeSidebar()}
        />
      </BranchBuildStatusProvider>
    </SidebarKeyboardShortcuts>
  );
};

export default withRunPipelineModal(BranchSidebar, 'runPipelineFromBranchPage');
